import React, { memo, useCallback, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import Styles from 'js/components/ManageCrops/FieldTextCanvas.module.less';
import { AutoSizer } from 'react-virtualized';
import useMapEvent from 'js/components/DataLayer/hooks/useMapEvent';
import throttle from 'lodash.throttle';
import { getFieldCenter } from 'js/helpers/MapsUtils';
import Box from '@mui/material/Box';
import { useGoogleMap } from 'js/context/GoogleMapContext';

const getTopLeftPixelCoords = (bounds, projection, zoom) => {
  const numTiles = 1 << zoom;

  const topLeft = new google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng());

  const topLeftWorldCoordinate = projection.fromLatLngToPoint(topLeft);

  return new google.maps.Point(
    topLeftWorldCoordinate.x * numTiles,
    topLeftWorldCoordinate.y * numTiles
  );
};

const mapToScreen = (projection, bounds, zoom, latLng) => {
  const numTiles = 1 << zoom;
  const worldCoordinate = projection.fromLatLngToPoint(latLng);
  const pixelCoordinate = new google.maps.Point(
    worldCoordinate.x * numTiles,
    worldCoordinate.y * numTiles
  );

  const topLeftPixelCoordinate = getTopLeftPixelCoords(bounds, projection, zoom);

  return new google.maps.Point(
    pixelCoordinate.x - topLeftPixelCoordinate.x,
    pixelCoordinate.y - topLeftPixelCoordinate.y
  );
};

const FieldTextCanvas = (props: FieldTextCanvas.propTypes) => {
  const googleMap = useGoogleMap();
  const canvasRef = useRef(null);

  const handler = useCallback(
    throttle(() => {
      if (!canvasRef.current) {
        return;
      }

      const ctx = canvasRef.current.getContext('2d');
      const width = canvasRef.current.width;
      const height = canvasRef.current.height;

      if (googleMap) {
        const proj = googleMap.getProjection();
        const bounds = googleMap.getBounds();
        const zoom = googleMap.getZoom();

        if (proj && bounds && zoom) {
          ctx.clearRect(0, 0, width, height);

          props.fields.forEach((field) => {
            const center = getFieldCenter(field);
            const latLng = new google.maps.LatLng(center.lat, center.lng);
            const point = mapToScreen(proj, bounds, zoom, latLng);

            if (point.x >= 0 && point.x <= width && point.y >= 0 && point.y <= height) {
              const lines = props.renderField(field, zoom);

              ctx.beginPath();

              lines.forEach(({ text, textAlign, color, fontSize, fontWeight }, idx) => {
                ctx.save();
                ctx.fillStyle = color;
                ctx.textAlign = textAlign;
                ctx.font = `${fontWeight} ${fontSize}px Roboto`;
                ctx.shadowColor = 'rgba(0,0,0,1)';
                ctx.shadowBlur = 2;
                ctx.strokeText(text, point.x, point.y + idx * fontSize);
                ctx.shadowColor = 'rgba(0,0,0,0)';
                ctx.shadowBlur = 0;
                ctx.fillText(text, point.x, point.y + idx * fontSize);

                ctx.restore();
              });
            }
          });
        }
      } else {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, width, height);
      }
    }, 10),
    [googleMap, props.fields, props.renderField]
  );

  useMapEvent(googleMap, 'zoom_changed', handler);
  useMapEvent(googleMap, 'center_changed', handler);
  useMapEvent(googleMap, 'idle', handler);

  useEffect(handler, [props.fields]);

  useEffect(() => {
    if (googleMap) {
      handler();
    }
  }, [googleMap]);

  return (
    <AutoSizer>
      {({ height, width }) => (
        <Box
          sx={{
            width: width,
            height: height,
            display: 'flex',
          }}
        >
          <canvas ref={canvasRef} className={Styles.Canvas} width={width} height={height} />
        </Box>
      )}
    </AutoSizer>
  );
};

FieldTextCanvas.propTypes = {
  fields: PropTypes.array,
  renderField: PropTypes.func,
};

export default memo(FieldTextCanvas);
